import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Compass, ArrowUpRight, Film, GraduationCap, CalendarDays, Footprints, Wallet, Banknote, ChevronDown, Search } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { api, resolveImg } from "@/lib/api";
import { IMAGES, JOURNEY_FORMATS, EXPERIENCE_CATEGORIES, experiencePlace } from "@/lib/content";

const STATUS_LINE = { OPEN: "Open for applications", UPCOMING: "Upcoming", "SOLD OUT": "Participation closed", COMPLETED: "Completed" };

const DAY_RANGES = { "1-3 days": [1, 3], "4-7 days": [4, 7], "8-14 days": [8, 14], "15+ days": [15, Infinity] };
const COST_RANGES = { "Under ₹1,00,000": [0, 100000], "₹1L-₹5L": [100000, 500000], "₹5L-₹15L": [500000, 1500000], "₹15L+": [1500000, Infinity] };
const SEARCH_DEFAULTS = { format: "Any", days: "Any", activity: "Any", type: "Any", cost: "Any" };

export function matchesSearch(j, s) {
  if (s.format !== "Any" && j.format !== s.format) return false;
  if (s.days !== "Any") {
    const [lo, hi] = DAY_RANGES[s.days];
    if (!(j.duration_days >= lo && j.duration_days <= hi)) return false;
  }
  if (s.activity !== "Any") {
    const hay = [...(j.activities || []), ...(j.categories || [])].map((a) => a.toLowerCase());
    if (!hay.includes(s.activity.toLowerCase())) return false;
  }
  if (s.type === "Paid" && !(j.price > 0)) return false;
  if (s.type === "Volunteering" && j.price > 0) return false;
  if (s.cost !== "Any") {
    const [lo, hi] = COST_RANGES[s.cost];
    if (!(Number(j.price || 0) >= lo && Number(j.price || 0) <= hi)) return false;
  }
  return true;
}

function SearchField({ icon: Icon, value, onChange, options, testid, label }) {
  return (
    <label className="relative flex items-center gap-3 flex-1 min-w-0 px-5 py-4 lg:py-3 border-b lg:border-b-0 lg:border-r border-ptf-primary/10 last:border-0 cursor-pointer">
      <Icon className="h-5 w-5 shrink-0 text-ptf-secondary" />
      <span className="sr-only">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full appearance-none bg-transparent text-sm font-medium text-ptf-primary outline-none pr-6 cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-4 h-4 w-4 text-ptf-primary/40" />
    </label>
  );
}

function ExperienceSearch({ journeys, onSearch }) {
  const [f, setF] = useState(SEARCH_DEFAULTS);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const activities = useMemo(() => {
    const s = new Set();
    journeys.forEach((j) => [...(j.activities || []), ...(j.categories || [])].forEach((a) => s.add(a)));
    return Array.from(s).sort();
  }, [journeys]);
  const opt = (arr, anyLabel) => [{ value: "Any", label: anyLabel }, ...arr.map((v) => (typeof v === "string" ? { value: v, label: v } : v))];

  return (
    <form
      data-testid="experience-search"
      onSubmit={(e) => { e.preventDefault(); onSearch(f); }}
      className="bg-white border border-ptf-primary/15 rounded-2xl lg:rounded-full shadow-[0_10px_40px_-12px_rgba(12,50,68,0.25)] p-2 flex flex-col lg:flex-row lg:items-stretch"
    >
      <SearchField icon={Compass} label="Format" testid="search-format" value={f.format} onChange={set("format")}
        options={opt([{ value: "TRAIL", label: "Trail · 1-3 days" }, { value: "QUEST", label: "Quest · 4-7 days" }, { value: "EXPEDITION", label: "Expedition · 8+ days" }], "Trail, Quest or Expedition")} />
      <SearchField icon={CalendarDays} label="Days" testid="search-days" value={f.days} onChange={set("days")}
        options={opt(Object.keys(DAY_RANGES), "Any duration")} />
      <SearchField icon={Footprints} label="Activity" testid="search-activity" value={f.activity} onChange={set("activity")}
        options={opt(activities, "Any activity")} />
      <SearchField icon={Wallet} label="Type" testid="search-type" value={f.type} onChange={set("type")}
        options={opt(["Paid", "Volunteering"], "Paid & volunteering")} />
      <SearchField icon={Banknote} label="Cost" testid="search-cost" value={f.cost} onChange={set("cost")}
        options={opt(Object.keys(COST_RANGES), "Any contribution")} />
      <button
        type="submit"
        data-testid="search-submit"
        className="shrink-0 m-1 inline-flex items-center justify-center gap-2 bg-ptf-secondary text-white rounded-xl lg:rounded-full px-8 py-4 lg:py-0 text-[0.72rem] font-semibold uppercase tracking-[0.2em] hover:bg-ptf-primary transition-colors"
      >
        <Search className="h-4 w-4" /> Search
      </button>
    </form>
  );
}

export function ExperienceCard({ j, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <Link to={`/exploration/${j.id}`} data-testid={`journey-card-${j.id}`} className="group h-full flex flex-col border border-ptf-primary/12 overflow-hidden hover:border-ptf-primary/30 transition-colors bg-white">
        <div className="relative aspect-[16/10] overflow-hidden bg-ptf-primary/5">
          {j.images?.[0] && (
            <img src={resolveImg(j.images[0])} alt={j.title} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
          )}
          {j.is_flagship && (
            <span data-testid="flagship-badge" className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-ptf-primary/90 text-ptf-bg px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.14em]">
              <Film className="h-3.5 w-3.5" /> Documentary Expedition
            </span>
          )}
        </div>
        <div className="p-7 flex flex-col flex-1">
          <p className="text-[0.6rem] uppercase tracking-[0.24em] text-ptf-secondary mb-3">Planet Trotter Field Experience</p>
          <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary leading-none group-hover:text-ptf-secondary transition-colors">{j.title}</h3>
          <p className="mt-3 text-[0.65rem] uppercase tracking-[0.18em] text-ptf-text/60">
            {[experiencePlace(j), j.duration_days > 0 ? `${j.duration_days} days` : "Dates to be announced"].filter(Boolean).join(" · ")}
          </p>
          {j.categories?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {j.categories.slice(0, 4).map((c) => (
                <span key={c} className="text-[0.58rem] uppercase tracking-[0.12em] border border-ptf-primary/20 text-ptf-text/60 px-2 py-1">{c}</span>
              ))}
            </div>
          )}
          {j.summary && <p className="mt-4 text-sm text-ptf-text/70 leading-relaxed flex-1 line-clamp-3">{j.summary}</p>}
          <div className="mt-5 pt-4 border-t border-ptf-primary/10 flex items-center justify-between">
            <span className="text-[0.62rem] uppercase tracking-[0.14em] text-ptf-text/50" data-testid="card-status-line">{STATUS_LINE[j.status] || j.status}</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ptf-primary">
              Explore <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

function Distinction() {
  return (
    <section className="bg-ptf-bg pt-16 md:pt-24">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Reveal>
          <Link to="/programmes" data-testid="distinction-programmes" className="group block border border-ptf-primary/15 bg-white p-8 md:p-10 h-full hover:border-ptf-primary/40 transition-colors">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-secondary">Programmes</p>
            <h3 className="mt-3 font-display uppercase text-3xl tracking-tight text-ptf-primary">Work we undertake.</h3>
            <p className="mt-4 text-sm text-ptf-text/70 leading-relaxed">Impact-oriented initiatives across conservation, communities, education and livelihoods, run by the Foundation and its partners.</p>
            <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ptf-primary">See the programmes <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
          </Link>
        </Reveal>
        <Reveal delay={0.08}>
          <div data-testid="distinction-experiences" className="border border-ptf-primary bg-ptf-primary text-ptf-bg p-8 md:p-10 h-full">
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-accent">Field Experiences</p>
            <h3 className="mt-3 font-display uppercase text-3xl tracking-tight">Ways people can participate, learn and explore.</h3>
            <p className="mt-4 text-sm text-ptf-bg/75 leading-relaxed">Immersive field journeys that combine travel with conservation, photography, research, education, documentary work and community interaction. They are not Foundation programmes, and they are never presented as one.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Exploration() {
  const [active, setActive] = useState("All");
  const [search, setSearch] = useState(SEARCH_DEFAULTS);
  const { data: journeys = [], isLoading } = useQuery({
    queryKey: ["journeys"],
    queryFn: async () => (await api.get("/journeys")).data,
  });

  const filtered = useMemo(
    () => journeys.filter((j) => (active === "All" || (j.categories || []).includes(active)) && matchesSearch(j, search)),
    [journeys, active, search]
  );

  return (
    <>
      <Seo title="Responsible Exploration" path="/exploration" description="Field experiences by Planet Trotter Foundation, designed around nature, conservation, responsible exploration, photography, learning and storytelling." />
      <PageHero
        eyebrow="Responsible Exploration"
        titleLines={["Explore", "with purpose."]}
        intro="Experiences designed around nature, conservation, responsible exploration, photography, learning and storytelling."
        image={IMAGES.hero}
      />

      <Distinction />

      <section className="bg-ptf-bg pt-16 md:pt-20">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-6 border-y border-ptf-primary/12 py-8">
            {JOURNEY_FORMATS.map((fFmt) => (
              <div key={fFmt.key} data-testid={`format-note-${fFmt.key.toLowerCase()}`}>
                <p className="font-display uppercase text-xl tracking-tight text-ptf-primary">{fFmt.label} <span className="text-ptf-text/40 text-sm font-sans normal-case tracking-normal ml-1">{fFmt.days}</span></p>
                <p className="mt-2 text-sm text-ptf-text/65 leading-relaxed">{fFmt.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-20">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <ExperienceSearch journeys={journeys} onSearch={setSearch} />
          <div className="flex flex-wrap gap-2 mt-10 mb-12">
            {["All", ...EXPERIENCE_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`exp-filter-${c.toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`text-[0.7rem] uppercase tracking-[0.14em] px-4 py-2 border transition-colors duration-300 ${
                  active === c ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="text-ptf-text/50">Loading…</p>
          ) : filtered.length === 0 ? (
            journeys.length > 0 ? (
              <Reveal>
                <div data-testid="journeys-filter-empty" className="border border-dashed border-ptf-primary/25 py-16 px-8 text-center">
                  <p className="font-display uppercase text-2xl tracking-tight text-ptf-primary">No field experiences match this search yet.</p>
                  <p className="mt-3 text-sm text-ptf-text/65">New experiences are added as routes, hosts and dates are confirmed.</p>
                </div>
              </Reveal>
            ) : (
            <Reveal>
              <div data-testid="journeys-empty" className="border border-dashed border-ptf-primary/25 py-24 px-8 text-center">
                <span className="grid place-items-center h-16 w-16 rounded-full bg-ptf-primary/5 mx-auto mb-6">
                  <Compass className="h-7 w-7 text-ptf-secondary" />
                </span>
                <h3 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary">The first field experiences are being designed.</h3>
                <p className="mt-4 max-w-lg mx-auto text-ptf-text/70 leading-relaxed">
                  Each one will be published here as routes, hosts and dates are confirmed.
                  Join the newsletter below to hear first.
                </p>
              </div>
            </Reveal>
            )
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((j, i) => (
                <ExperienceCard key={j.id} j={j} delay={(i % 3) * 0.06} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white border-y border-ptf-primary/10 py-16 md:py-20">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-secondary mb-4 flex items-center gap-3">
              <GraduationCap className="h-4 w-4" /> Learning & Training
            </p>
            <h2 className="font-display font-extrabold uppercase text-3xl md:text-5xl tracking-tight text-ptf-primary leading-[0.95]">
              Learn where the work happens.
            </h2>
            <p className="mt-5 max-w-2xl text-ptf-text/75 leading-relaxed">
              Every field experience connects with skills we intend to teach: wildlife photography,
              field documentation, expedition craft and conservation science. Courses are in
              development under Planet Trotter Learning & Training.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <PtfButton to="/learning" variant="outline" data-testid="exploration-learning-link">Explore the Themes</PtfButton>
          </div>
        </div>
      </section>

      <section className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-accent mb-5 flex items-center gap-3">
              <span className="h-px w-8 bg-ptf-accent inline-block" /> Documentary Expeditions
            </p>
            <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Expeditions that become films.
            </h2>
            <p className="mt-6 max-w-2xl text-ptf-bg/75 leading-relaxed">
              Our flagship expeditions are documentary-led. Participants join the field team,
              the journey is filmed, and the film carries the story of the place to
              festivals in India and abroad.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <PtfButton to="/flagship" variant="accent" data-testid="flagship-link">How Flagships Work</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
